/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {ENDPOINTS, METHODS} from '#utils/api';
import {getTokensFromCookies, setTokensToCookies} from '#utils/cookies';
import {LOCALE_CODES} from '../i18n/constants';
import {clearTokensAndLogout} from './query-client';

const BASE_URL = (
  process.env.NEXT_PUBLIC_BASE_URL || 'https://api.dev.purposeit.no'
).replace(/\/$/, '');

// Helper function to construct URLs properly
const buildUrl = (baseUrl: string, path: string): string => {
  // If path already starts with slash, use it as is
  if (path.startsWith('/')) {
    return `${baseUrl}${path}`;
  }
  // Otherwise, add slash between base and path
  return `${baseUrl}/${path}`;
};

// Mutex to prevent multiple simultaneous token refresh attempts
let refreshPromise: Promise<boolean> | null = null;
// Flag to prevent multiple redirect attempts
let isRedirecting = false;

// Helper function to logout and redirect to sign-in
function logoutAndRedirect() {
  if (typeof window === 'undefined') return;

  // Prevent multiple redirect attempts
  if (isRedirecting) {
    return;
  }

  // Clear tokens and storage
  // Build sign-in URL - always include locale prefix for consistency
  clearTokensAndLogout({
    keepSessionStorage: false,
    showToast: false,
    onLogout: () => {
      isRedirecting = true;
    },
    beforeRedirect: () => {
      // Reset flag after redirect starts
      isRedirecting = false;
    }
  });
}

// Helper function to check if a URL is a login endpoint
// Login endpoints should not trigger automatic token refresh or logout redirects
// as they are expected to fail with wrong credentials and should show error messages
function isLoginEndpoint(url: string): boolean {
  // Normalize the URL by removing leading/trailing slashes for comparison
  const normalizedUrl = url.replace(/^\/+|\/+$/g, '');

  // Check for exact matches or contains login/social
  return (
    normalizedUrl === 'login' ||
    normalizedUrl === 'login/social' ||
    normalizedUrl.startsWith('login/')
  );
}

// Token refresh function
async function refreshToken(): Promise<boolean> {
  // Get refresh token from cookies
  const {refreshToken: refreshTokenFromCookie} = getTokensFromCookies();

  if (!refreshTokenFromCookie) {
    return false;
  }

  try {
    const response = await fetch(buildUrl(BASE_URL, '/refresh-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refreshToken: refreshTokenFromCookie
      })
    });

    if (!response.ok) {
      // Refresh token failed - return false, caller will handle redirect
      return false;
    }

    const result = await response.json();

    // Try different possible response structures
    const {token, refresh_token} =
      result.data?.data || result.data || result || {};

    if (token) {
      try {
        await setTokensToCookies({
          token,
          refreshToken: refresh_token || refreshTokenFromCookie
        });
        return true;
      } catch (error) {
        // Failed to set tokens - return false
        return false;
      }
    }

    // No token in response - return false
    return false;
  } catch (error) {
    // Network or other error - return false
    return false;
  }
}

export const textBaseQuery = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<string> => {
  const {token} = getTokensFromCookies();

  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.text(); // same as responseHandler: response => response.text()
};

// Create a base fetch function with auth headers and automatic token refresh
async function baseFetch(url: string, options: RequestInit = {}, blob = false) {
  const {token} = getTokensFromCookies();
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    // Only set Content-Type if not FormData
    ...(isFormData ? {} : {'Content-Type': 'application/json'}),
    ...(options.headers as Record<string, string>)
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response = await fetch(buildUrl(BASE_URL, url), {
    ...options,
    headers
  });

  // Check if this is a login endpoint
  const isLogin = isLoginEndpoint(url);

  // Handle 401 errors
  if (response.status === 401) {
    // For login endpoints, skip automatic token refresh/logout redirect
    // Errors should pass through to be handled by the calling code
    if (isLogin) {
      // Let the error pass through - don't try to refresh or redirect
      // The error will be caught and displayed in the sign-in form
    } else {
      // For non-login endpoints, always attempt token refresh
      // If there's already a refresh in progress, wait for it
      if (refreshPromise) {
        const refreshResult = await refreshPromise;
        // Check if we still have a token (refresh might have failed and logged us out)
        const {token: newToken} = getTokensFromCookies();
        if (newToken) {
          // Preserve FormData handling in retry - don't set Content-Type if body is FormData
          const retryIsFormData = options.body instanceof FormData;
          const updatedHeaders = {
            ...(retryIsFormData ? {} : {'Content-Type': 'application/json'}),
            Authorization: `Bearer ${newToken}`,
            ...(options.headers as Record<string, string>)
          };
          response = await fetch(buildUrl(BASE_URL, url), {
            ...options,
            headers: updatedHeaders
          });
        }
        // If no token after waiting, refreshToken() already called logoutAndRedirect()
      } else {
        // Start a new refresh attempt - use mutex to prevent multiple simultaneous refreshes
        refreshPromise = (async () => {
          try {
            const refreshSuccess = await refreshToken();
            return refreshSuccess;
          } catch (error) {
            return false;
          } finally {
            refreshPromise = null;
          }
        })();

        const refreshSuccess = await refreshPromise;

        if (refreshSuccess) {
          // Refresh succeeded - retry the original request with new token
          const {token: newToken} = getTokensFromCookies();
          if (newToken) {
            const retryIsFormData = options.body instanceof FormData;
            const updatedHeaders = {
              ...(retryIsFormData ? {} : {'Content-Type': 'application/json'}),
              Authorization: `Bearer ${newToken}`,
              ...(options.headers as Record<string, string>)
            };
            response = await fetch(buildUrl(BASE_URL, url), {
              ...options,
              headers: updatedHeaders
            });
          } else {
            // No token after successful refresh - this shouldn't happen, but handle it
            if (!isRedirecting) {
              logoutAndRedirect();
            }
            throw new Error('Token refresh succeeded but no token found');
          }
        } else {
          // Refresh failed - redirect to sign-in (only once)
          if (!isRedirecting) {
            logoutAndRedirect();
          }
          throw new Error('Token refresh failed - redirecting to sign-in');
        }
      }
    }
  }

  // Check if response is ok and throw error if not
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw errorData;
  }

  if (blob) return response.blob();
  return response.json();
}

// API client with common methods
export const apiClient = {
  get: <T>(
    url: string,
    params?: Record<string, any> | {params: Record<string, any>},
    blob = false
  ): Promise<T> => {
    const searchParams = new URLSearchParams();

    // Handle both direct params and { params } structure
    let actualParams: Record<string, any> | undefined;
    if (params && 'params' in params) {
      actualParams = params.params;
    } else {
      actualParams = params as Record<string, any> | undefined;
    }

    if (actualParams) {
      Object.entries(actualParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle arrays by joining them with commas
          if (Array.isArray(value)) {
            searchParams.append(key, value.join(','));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = searchParams.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    return baseFetch(fullUrl, {method: METHODS.get}, blob);
  },

  post: <T>(
    url: string,
    data?: any,
    options?: RequestInit,
    blob = false
  ): Promise<T> => {
    return baseFetch(
      url,
      {
        method: METHODS.post,
        body: data
          ? data instanceof FormData
            ? data
            : JSON.stringify(data)
          : undefined,
        ...options
      },
      blob
    );
  },

  put: <T>(
    url: string,
    data?: any,
    options?: RequestInit,
    blob = false
  ): Promise<T> => {
    return baseFetch(
      url,
      {
        method: METHODS.put,
        body: data
          ? data instanceof FormData
            ? data
            : JSON.stringify(data)
          : undefined,
        ...options
      },
      blob
    );
  },

  delete: <T>(
    url: string,
    options?: RequestInit & {data?: any},
    blob = false
  ): Promise<T> => {
    const {data, ...restOptions} = options || {};
    return baseFetch(
      url,
      {
        method: METHODS.delete,
        body: data ? JSON.stringify(data) : undefined,
        ...restOptions
      },
      blob
    );
  },

  patch: <T>(
    url: string,
    data?: any,
    options?: RequestInit,
    blob = false
  ): Promise<T> => {
    return baseFetch(
      url,
      {
        method: METHODS.patch,
        body: data
          ? data instanceof FormData
            ? data
            : JSON.stringify(data)
          : undefined,
        ...options
      },
      blob
    );
  }
};

// Specialized API clients for different endpoints
export const categoryApiClient = {
  getOrgCategories: (params?: {search?: string}) =>
    apiClient.get(ENDPOINTS.category.organisationCategory, params),

  getB2CCategories: () => apiClient.get(ENDPOINTS.category.b2cCategories),

  getOrganisationB2CCategories: () =>
    apiClient.get(ENDPOINTS.category.organisationB2CCategories),

  getBenefitCategories: () => apiClient.get(ENDPOINTS.category.b2cCategories) // Same endpoint, different transformer
};

export const subscriptionsApiClient = {
  getSubscriptions: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.user.subscriptions, params),

  addSubscriptions: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.user.addSubscriptions, {params}),

  unsubscribe: (id: number) => apiClient.post(ENDPOINTS.user.unsubscribe(id)),

  bulkUnsubscribe: (ids: number[]) =>
    apiClient.post(ENDPOINTS.user.bulkUnsubscribe, {organisationIds: ids}),

  acceptInvitation: (id: number) =>
    apiClient.post(ENDPOINTS.user.acceptInvitation(id)),

  declineInvitation: (id: number) =>
    apiClient.post(ENDPOINTS.user.declineInvitation(id))
};

export const settingsApiClient = {
  getMerchantPendingOrganisations: () =>
    apiClient.get(ENDPOINTS.merchant.checkPendingOrgs),

  // deleting media
  deleteMemberAvatar: () => apiClient.delete(ENDPOINTS.user.avatar),

  deleteOrganisationAvatar: () =>
    apiClient.delete(ENDPOINTS.organisation.avatar),

  deleteMerchantAvatar: () => apiClient.delete(ENDPOINTS.merchant.avatar),

  deleteOrganisationLogo: () => apiClient.delete(ENDPOINTS.organisation.logo),

  deleteMerchantLogo: () => apiClient.delete(ENDPOINTS.merchant.logo),

  deleteOrganisationBanner: () =>
    apiClient.delete(ENDPOINTS.organisation.banner),

  // user settings
  getUserSettings: () => apiClient.get(ENDPOINTS.user.settings),

  getUserLanguage: () => apiClient.get(ENDPOINTS.user.language),
  editUserLanguage: (locale: LOCALE_CODES) =>
    apiClient.patch(ENDPOINTS.user.language, {language: locale}),

  editUserProfileSettings: (data: FormData) =>
    apiClient.patch(ENDPOINTS.user.settingsProfile, data),

  editUserPreferencesSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.user.settingsPreferences, data),

  // merchant settings
  getMerchantSettings: () => apiClient.get(ENDPOINTS.merchant.settings),

  getMerchantLanguage: () => apiClient.get(ENDPOINTS.merchant.language),
  editMerchantLanguage: (locale: LOCALE_CODES) =>
    apiClient.patch(ENDPOINTS.merchant.language, {language: locale}),

  editOrganisationSettingsAddress: (data: any, options) =>
    apiClient.put(ENDPOINTS.organisation.settings_address, data, options),

  getWebshopSettings: () =>
    apiClient.get(ENDPOINTS.organisation.webshop_settings),

  editMerchantProfileSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.merchant.settingsProfile, data),

  editMerchantBusinessProfileSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.merchant.settingsBusinessProfile, data),

  editMerchantSecuritySettings: (data: any) =>
    apiClient.patch(ENDPOINTS.merchant.settingsSecurity, data),

  editMerchantValidationSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.merchant.settingsValidation, data),

  editMerchantLocations: (data: {addresses: object[]}) =>
    apiClient.put(ENDPOINTS.merchant.address, data),

  getMerchantLocations: () => apiClient.get(ENDPOINTS.merchant.locations),

  editMerchantMedia: (data: FormData) =>
    apiClient.put(ENDPOINTS.merchant.media, data),

  editMerchantB2CCategory: (data: Record<string, any>) =>
    apiClient.patch(ENDPOINTS.merchant.b2c_category, data),

  // organisation settings
  getOrganisationSettings: () => apiClient.get(ENDPOINTS.organisation.settings),

  getOrganisationLanguage: () => apiClient.get(ENDPOINTS.organisation.language),
  editOrganisationLanguage: (locale: LOCALE_CODES) =>
    apiClient.patch(ENDPOINTS.organisation.language, {language: locale}),

  editWebshopSettings: (data: FormData) =>
    apiClient.put(ENDPOINTS.organisation.webshop_settings, data),

  editOrganisationProfileSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.organisation.settingsProfile, data),

  editOrganisationBusinessProfileSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.organisation.settingsBusinessProfile, data),

  editOrganisationSecuritySettings: (data: any) =>
    apiClient.patch(ENDPOINTS.organisation.settingsSecurity, data),

  editOrganisationValidationSettings: (data: any) =>
    apiClient.patch(ENDPOINTS.organisation.settingsValidation, data),

  editOrganisationAddress: (data: any) =>
    apiClient.patch(ENDPOINTS.organisation.address, data),

  editOrganisationMedia: (data: FormData) =>
    apiClient.put(ENDPOINTS.organisation.media, data), // avatar = avatar

  editOrganisationLogo: (data: FormData) =>
    apiClient.put(ENDPOINTS.organisation.webshop_logo, data), // logo = business/webshop logo

  // Benefits
  uploadBenefitImage: (data: FormData) =>
    apiClient.post(ENDPOINTS.benefit.uploadImage, data, {}),

  createBenefit: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.benefit.create, data),

  editMerchantBenefit: (data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.edit(String(data.id)), data)
};

export const merchantApiClient = {
  getMerchantOrganisation: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.merchant.organisation, {params}),

  getMerchantOrganisationRequests: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.merchant.organisationRequest, {params}),

  getMerchantOrganisationRequestById: (id: string | number) =>
    apiClient.get(ENDPOINTS.merchant.organisationRequestById(id)),

  getMerchantOrganisationById: (
    id: string | number,
    params: Record<string, any>
  ) => apiClient.get(ENDPOINTS.merchant.orhanisationById(id), {params}),

  subscribeMerchantOrganisation: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.merchant.subscribeOrganisation(data.id), {}),

  unsubscribeMerchantOrganisation: (id: string | number) =>
    apiClient.post(ENDPOINTS.merchant.unsubscribeOrganisation(id), {}),

  bulkUnsubscribeMerchantOrganisation: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.merchant.unsubscribeOrganisationsBulk, {
      organisationIds: data.ids || data
    }),

  acceptMerchantOrganisationRequest: (id: string | number) =>
    apiClient.post(ENDPOINTS.merchant.acceptOrganisationRequest(id)),

  declineMerchantOrganisationRequest: (id: string | number) =>
    apiClient.post(ENDPOINTS.merchant.declineOrganisationRequest(id)),

  getAllOrganisations: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.merchant.allOrganisations, {params}),

  getAddOrganisations: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.merchant.addOrganisation, {params}),

  getMerchantSuggestions: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.merchant.searchSuggestions, {params})
};

export const benefitsApiClient = {
  getBenefit: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.base, {params}),

  getPopularBenefit: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.popular, {params}),

  getGlobalSearchBenefits: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.globalSearch, {params}),

  getBenefitById: (id: string, params: Record<string, any> = {}) =>
    apiClient.get(ENDPOINTS.benefit.benefitById(id), {params}),

  getBenefitByIdUnauthorized: (id: string, params: Record<string, any> = {}) =>
    apiClient.get(ENDPOINTS.benefit.benefitByIdUnauthorized(id), {params}),

  getUserBenefit: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.user.benefits, {params}),

  getBenefitAvailability: (id: string) =>
    apiClient.get(ENDPOINTS.benefit.benefitAvailability(id)),

  getBenefitDistribution: (id: string) =>
    textBaseQuery(ENDPOINTS.benefit.getDistributionCsv(id)),

  updateBenefitAvailability: (id: string, data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.benefitAvailability(id), data),

  // Additional methods from the second definition
  getOrganisationBenefits: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.organisationBenefits, {params}),
  getOrganisationBenefitsUniversal: (id, params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.universalOrganisationBenefits(id), {
      params
    }),

  getMerchantBenefitsForOrganisation: (
    merchantId: number,
    params: Record<string, any>
  ) =>
    apiClient.get(
      ENDPOINTS.benefit.merchantBenefitsForOrganisation(String(merchantId)),
      {params}
    ),

  getOrganisationBenefitById: (
    id: string | number,
    params: Record<string, any> = {}
  ) =>
    apiClient.get(ENDPOINTS.benefit.organisationBenefitById(String(id)), {
      params
    }),

  getMerchantBenefits: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.merchantBenefits, {params}),
  getCheckPublicBenefit: (params: Record<string, any>) =>
    apiClient.post(ENDPOINTS.benefit.checkPublicBenefit, params),
  getMerchantBenefitsUniversal: (id, params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.universalMerchantBenefits(id), {
      params
    }),
  getMemberBenefitById: (
    id: string | number,
    params: Record<string, any> = {}
  ) =>
    apiClient.get(ENDPOINTS.benefit.memberBenefitById(String(id)), {
      params
    }),
  getMerchantBenefitById: (
    id: string | number,
    params: Record<string, any> = {}
  ) =>
    apiClient.get(ENDPOINTS.benefit.merchantBenefitById(String(id)), {
      params
    }),

  createBenefit: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.benefit.create, data),

  updateBenefit: (id: string | number, data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.edit(String(id)), data),
  updateBenefitLocations: (id: string | number, data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.editAddress(String(id)), data),
  updateMerchantBenefitStatus: (data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.deactivateMerchantBenefit, data),
  updateOrganisationBenefitStatus: (data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.benefit.deactivateOrganisationBenefit, data),
  uploadBenefitImage: (data: FormData) =>
    apiClient.post(ENDPOINTS.benefit.uploadImage, data, {}),

  getRedeemedBenefits: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.memberRedeemedBenefits, {params}),

  getExpiredBenefits: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.memberExpiredBenefits, {params}),

  redeemBenefit: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.benefit.redeem, data),

  getMerchantBenefitsStatistics: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.merchantBenefitsStatistics, {
      params
    }),

  getMerchantBenefitsStatisticsOverall: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.benefit.merchantBenefitsStatisticsOverall, {
      params
    }),

  getMerchantBenefitStatisticById: (
    id: string | number,
    data: Record<string, any>
  ) =>
    apiClient.get(
      ENDPOINTS.benefit.getMerchantBenefitStatistic(String(id)),
      data
    ),
  getMerchantBenefitRedemptionRateById: (
    id: string | number,
    data: Record<string, any>
  ) =>
    apiClient.get(
      ENDPOINTS.benefit.getMerchantBenefitRedemptionRate(String(id)),
      data
    ),
  getMerchantBenefitTotalRedemptionsById: (
    id: string | number,
    data: Record<string, any>
  ) =>
    apiClient.get(
      ENDPOINTS.benefit.getMerchantBenefitTotalRedemption(String(id)),
      data
    )
};

export const authApiClient = {
  // Registration endpoints
  signUpMember: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.signUpMember, data),

  signUpOrganisation: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.signUpOrg, data),

  signUpMerchant: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.signUpMerchant, data),

  updateBusinessInfo: data =>
    apiClient.post(ENDPOINTS.auth.updateBusinessInfo, data, {}),

  updateBusinessInfoLogo: (params, data: FormData) =>
    apiClient.post(
      `${ENDPOINTS.auth.updateBusinessInfoLogo}?entityId=${params.entityId}&entityType=${params.entityType}`,
      data,
      {}
    ),

  updateContactPerson: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.updateContactPerson, data),

  updateB2CCategory: (id: number | string, data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.updateB2CCategory(id), data),

  // OTP endpoints
  sendOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.sendOtp, data),

  resendPhoneOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.sendPhoneOtp, data),

  resendOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.resendOtp, data),

  verifyOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.verifyOtp, data),

  // Verification endpoints
  verifyOrgCode: (id: number | string) =>
    apiClient.get(ENDPOINTS.auth.verifyOrgCode(id)),

  checkMerchantId: (id: number | string) =>
    apiClient.get(ENDPOINTS.auth.checkMerchantId(id)),

  checkBusinessNumber: (id: number | string) =>
    apiClient.get(ENDPOINTS.auth.checkBusinessNumber(id)),

  // Login endpoints
  login: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.login, data),

  loginSocial: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.loginSocial, data),

  // Password management
  resetPasswordRequest: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.resetPasswordRequest, data),

  resetPassword: (data: Record<string, any>) =>
    apiClient.put(
      `${ENDPOINTS.auth.resetPassword}?resetPasswordToken=${data.token}`,
      {
        newPassword: data.newPassword
      }
    ),

  newPassword: (data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.auth.changePassword, data),

  // Recovery
  changeRecoveryPhoneRequest: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.changeRecoveryPhoneRequest, data),

  changeRecoveryPhone: (data: Record<string, any>) =>
    apiClient.put(
      `${ENDPOINTS.auth.changeRecoveryPhone}?changePhoneToken=${data.token}`,
      {
        newPhone: data.newPhone
      }
    ),

  sendRecoveryEmailOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.sendRecoveryEmailOtp, data),

  resendRecoveryEmailOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.resendRecoveryEmailOtp, data),

  verifyRecoveryEmailOtp: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.verifyRecoveryEmailOtp, data),

  changeRecoveryEmail: (data: Record<string, any>) =>
    apiClient.put(ENDPOINTS.auth.changeEmail, data),

  // Social verification
  verifyGoogle: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.verifyGoogle, data),

  socialPrefill: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.auth.socialPrefill, data),

  // Account management
  deleteMemberAccount: () => apiClient.delete(ENDPOINTS.user.me),

  deleteOrganisationAccount: () => apiClient.delete(ENDPOINTS.organisation.me),

  deleteMerchantAccount: () => apiClient.delete(ENDPOINTS.merchant.me),

  checkRestrictionStatus: () => apiClient.get(ENDPOINTS.auth.restrictionStatus)
};

export const api1881Client = {
  searchByPhone: (params: {phone: string}) =>
    apiClient.get(ENDPOINTS.api1881.searchByPhone, params),

  searchOrganisationByNumber: (params: {organisationNumber: string}) =>
    apiClient.get(
      ENDPOINTS.api1881.searchOrganisationByNumber(params.organisationNumber),
      params
    )
};

export const organisationApiClient = {
  getSuggested: (params: {search?: string}) =>
    apiClient.get(ENDPOINTS.organisation.suggested, params),

  getGlobalSuggestions: (params: {search?: string}) =>
    apiClient.get(ENDPOINTS.organisation.globalSuggestion, params),

  getOrganisation: (params: {
    search?: string;
    limit?: number;
    pageNumber?: number;
  }) => apiClient.get(ENDPOINTS.organisation.base, params),

  getOrganisationsByCategory: (
    id: number,
    params: {search?: string; limit?: number; pageNumber?: number}
  ) =>
    apiClient.get(ENDPOINTS.organisation.organisationsByCategory(id), params),

  getAllSubscriptions: (params: {
    search?: string;
    limit?: number;
    categoryId?: number;
    pageNumber?: number;
  }) => apiClient.get(ENDPOINTS.user.addSubscriptions, params),

  getOrganisationSuggestions: (params: {search?: string}) =>
    apiClient.get(ENDPOINTS.organisation.searchSuggestions, params),

  getFeedOrganisations: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.feed, params),

  getPopular: () => apiClient.get(ENDPOINTS.organisation.popular),

  getOrganisationCategories: (id: string | number) =>
    apiClient.get(ENDPOINTS.organisation.getCategories(id)),

  getOrganisationMerchants: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.merchant, {params}),

  getOrganisationMerchantsRequests: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.merchantRequest, {params}),

  getOrganisationMerchantRequestById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.merchantRequestById(id), params),

  getAllMerchants: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.addMerchant, {params}),

  getOrganisationMerchantById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.merchantById(id), params),

  getOrganisationMemberById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.member(id), {params}),

  subscribeMerchant: (id: number) =>
    apiClient.post(ENDPOINTS.organisation.subscribeMerchant(id)),
  unsubscribeMerchant: (id: number) =>
    apiClient.post(ENDPOINTS.organisation.unsubscribeMerchant(id)),

  bulkUnsubscribeMerchant: (ids: number[]) =>
    apiClient.post(ENDPOINTS.organisation.unsubscribeMerchantsBulk, {
      merchantIds: ids
    }),

  retreiveOrgCode: (organisationId: number) =>
    apiClient.post(ENDPOINTS.organisation.retrieveCodeUregistered, {
      organisationId
    }),

  getGlobalSearchOrganisations: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.globalSearch, {params}),

  getOrganisationMembers: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.members, {params}),

  getOrganisationPendingMembers: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.membersPending, {params}),

  getOrganisationPendingMerchants: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.checkPendingMerchants, {params}),

  getOrganisationInvitedMembers: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.organisation.membersInvited, {params}),

  deleteOrganisationMember: (data: Record<string, any>) =>
    apiClient.delete(ENDPOINTS.organisation.members, {data}),

  inviteOrganisationMember: (data: Record<string, any>) =>
    apiClient.post(ENDPOINTS.organisation.inviteByEmail, data),

  uploadMemberCsv: (language: string, formData: FormData) =>
    apiClient.post(
      `${ENDPOINTS.organisation.uploadMemberCsv}?language=${language}`,
      formData,
      {}
    ),

  getMemberQrCode: () => apiClient.post(ENDPOINTS.organisation.getMemberQrCode),

  getOrganisationCategoryById: (
    id: string | number,
    categoryId: string | number,
    params: Record<string, any> = {}
  ) =>
    apiClient.get(ENDPOINTS.organisation.getCategoryById(id, categoryId), {
      params
    }),

  getOrganisationById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.getById(id), {params}),

  getUnauthorizedOrganisationById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.getByIdUnauthorized(id), {params}),

  getOrganisationDetailsById: (
    id: string | number,
    params: Record<string, any> = {}
  ) => apiClient.get(ENDPOINTS.organisation.getDetailsById(id), {params}),

  subscribeOrganisation: (id: string | number) =>
    apiClient.post(ENDPOINTS.organisation.subscribe(id)),

  unsubscribeOrganisation: (id: string | number) =>
    apiClient.post(ENDPOINTS.organisation.unsubscribe(id)),

  // Generic get method for other endpoints
  get: (url: string, params?: Record<string, any>) =>
    apiClient.get(url, params),

  // Generic post method for auth endpoints
  post: (url: string, data?: any) => apiClient.post(url, data),

  // Generic delete method
  delete: (url: string) => apiClient.delete(url),

  // Mutation methods
  subscribe: (organisationId: string | number) =>
    apiClient.post(
      ENDPOINTS.organisation.subscribe(String(organisationId)),
      {}
    ),

  unsubscribe: (organisationId: string | number) =>
    apiClient.delete(
      ENDPOINTS.organisation.unsubscribe(String(organisationId)),
      {}
    ),

  acceptMerchantOrganisationRequest: (id: string | number) =>
    apiClient.post(ENDPOINTS.organisation.acceptMerchantRequest(id)),

  declineMerchantOrganisationRequest: (id: string | number) =>
    apiClient.post(ENDPOINTS.organisation.declineMerchantRequest(id))
};

export const notificationsApiClient = {
  getNotifications: (params: Record<string, any>) =>
    apiClient.get(ENDPOINTS.notification.base, params),

  getNotificationById: (id: string | number) =>
    apiClient.get(ENDPOINTS.notification.notificationById(String(id))),

  getUnreadNotificationExists: () =>
    apiClient.get(ENDPOINTS.notification.unreadExists())
};

export const receiptsApiClient = {
  createSession: () => apiClient.post(ENDPOINTS.receipts.createSession),

  getLinkedCards: () => apiClient.get(ENDPOINTS.receipts.linkedCards),

  unlinkCard: (cardId: number | string) =>
    apiClient.post(ENDPOINTS.receipts.unenrollCard(cardId), {cardId})
};

export const geoapifyApiClient = {
  getAutocompleteAddresses: ({
    lang,
    search
  }: {
    lang: LOCALE_CODES;
    search: string;
  }) =>
    apiClient.get(ENDPOINTS.geoapify.autocomplete, {
      params: {
        lang,
        search
      }
    })
};
